import { CornerDownLeft, RotateCcw, Square } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { renderMarkdown } from "@navibiz/chat-core/markdown";
import type { ChatEvent, ChatLink, Handoff, TurnDebug, UsageReport } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { fmt } from "../../lib/format";
import { keys, useKnowledge, useMe } from "../../lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { EmptyState } from "../../ui/Feedback";
import { Kbd, Segmented } from "../../ui/Misc";
import { useProjectContext } from "./ProjectLayout";
import styles from "./PlaygroundTab.module.css";

interface Turn {
  id: number;
  question: string;
  answer: string;
  streaming: boolean;
  links?: ChatLink[];
  handoff?: Handoff;
  usage?: UsageReport;
  debug?: TurnDebug;
  error?: string;
}

const HANDOFF_LABEL: Record<string, string> = { order: "Siap pesan → WhatsApp", unknown: "Tidak tahu → WhatsApp", human: "Minta manusia → WhatsApp" };

/**
 * The test bench. Same pipeline as production — retrieval, prompt, model,
 * marker filter. Staff test drafts at Navibiz's cost; a client always talks
 * to the live bot and pays from saldo, like any visitor.
 */
export function PlaygroundTab() {
  const { project } = useProjectContext();
  const client = useMe().data!.role === "client";
  const queryClient = useQueryClient();
  // Entry titles label the inspector's chunks; clients have no access to the knowledge base.
  const knowledge = useKnowledge(project.id, !client);
  const [source, setSource] = useState<"draft" | "published">(client ? "published" : "draft");
  const [locale, setLocale] = useState<"id" | "en">(project.defaultLocale);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [input, setInput] = useState("");
  const abort = useRef<AbortController | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const busy = turns.at(-1)?.streaming ?? false;

  const titles = useMemo(() => new Map((knowledge.data?.entries ?? []).map((entry) => [entry.id, entry.title])), [knowledge.data]);
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [turns]);

  const reset = () => {
    abort.current?.abort();
    setTurns([]);
    setSelected(null);
    setConversationId(undefined);
  };

  async function ask(question: string) {
    if (!question.trim() || busy) return;
    const id = Date.now();
    setInput("");
    setTurns((current) => [...current, { id, question, answer: "", streaming: true }]);
    setSelected(id);
    const patch = (update: Partial<Turn> | ((turn: Turn) => Partial<Turn>)) =>
      setTurns((current) => current.map((turn) => (turn.id === id ? { ...turn, ...(typeof update === "function" ? update(turn) : update) } : turn)));

    abort.current = new AbortController();
    try {
      for await (const event of api.stream<ChatEvent>(`/projects/${project.id}/playground`, { message: question, conversationId, knowledge: source, locale }, abort.current.signal)) {
        if (event.type === "meta") setConversationId(event.conversationId);
        else if (event.type === "delta") patch((turn) => ({ answer: turn.answer + event.text }));
        else if (event.type === "links") patch({ links: event.links });
        else if (event.type === "handoff") patch({ handoff: event.handoff });
        else if (event.type === "done") patch({ usage: event.usage, debug: event.debug });
        else if (event.type === "error") patch({ error: event.error.message });
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) patch({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      patch({ streaming: false });
      abort.current = null;
      // A client's answer came out of saldo: the header and Saldo tab should show it.
      if (client) void queryClient.invalidateQueries({ queryKey: keys.project(project.id) });
    }
  }

  const inspected = turns.find((turn) => turn.id === selected) ?? turns.at(-1);
  const suggestions = project.persona.suggestions[locale];

  return (
    <div className={styles.layout}>
      <Card
        className={styles.chatCard}
        title="Playground"
        description={
          client
            ? `Menjawab dari pengetahuan yang tayang${project.publishedVersion ? ` (v${project.publishedVersion})` : ""} dan memotong saldo seperti percakapan di widget.`
            : "Ditanggung Navibiz: pemakaian di sini tidak memotong saldo klien."
        }
        actions={
          <>
            {!client && (
              <Segmented
                label="Sumber pengetahuan"
                value={source}
                onChange={(value) => {
                  setSource(value);
                  reset();
                }}
                options={[
                  { value: "draft", label: "Draf" },
                  { value: "published", label: project.publishedVersion ? `Tayang v${project.publishedVersion}` : "Tayang" },
                ]}
              />
            )}
            <Segmented label="Bahasa" value={locale} onChange={setLocale} options={[{ value: "id", label: "ID" }, { value: "en", label: "EN" }]} />
            <Button size="sm" variant="ghost" icon={<RotateCcw aria-hidden />} onClick={reset} aria-label="Percakapan baru" />
          </>
        }
        flush
      >
        <div className={styles.log} ref={logRef} aria-live="polite">
          <div className={`${styles.bubble} ${styles.bot}`} dangerouslySetInnerHTML={{ __html: renderMarkdown(project.persona.greeting[locale]) }} />
          {turns.length === 0 && suggestions.length > 0 && (
            <div className={styles.chips}>
              {suggestions.map((suggestion) => (
                <button key={suggestion} type="button" onClick={() => ask(suggestion)}>
                  {suggestion}
                </button>
              ))}
            </div>
          )}
          {turns.map((turn) => (
            <div key={turn.id} className={styles.turn}>
              <div className={`${styles.bubble} ${styles.user}`}>{turn.question}</div>
              <button
                type="button"
                className={`${styles.bubble} ${styles.bot} ${turn.id === inspected?.id ? styles.inspected : ""} ${turn.error ? styles.error : ""}`}
                onClick={() => setSelected(turn.id)}
                aria-label="Lihat detail jawaban ini"
              >
                {turn.error ? (
                  turn.error
                ) : turn.answer ? (
                  <span dangerouslySetInnerHTML={{ __html: renderMarkdown(turn.answer) }} />
                ) : (
                  <span className={styles.typing} aria-label="Mengetik">
                    <i />
                    <i />
                    <i />
                  </span>
                )}
              </button>
              {turn.links && turn.links.length > 0 && (
                <div className={styles.links}>
                  {turn.links.map((link) => (
                    <a key={link.id} className={styles.link} href={link.url} target="_blank" rel="noreferrer">
                      {link.imageUrl && <img src={link.imageUrl} alt="" onError={(event) => (event.currentTarget.style.display = "none")} />}
                      <span>
                        <strong>{link.title}</strong>
                        {link.price && <em>{link.price}</em>}
                        <small>{link.reason}</small>
                      </span>
                    </a>
                  ))}
                </div>
              )}
              <div className={styles.markers}>
                {turn.handoff && <Badge tone="accent">{HANDOFF_LABEL[turn.handoff.reason]}</Badge>}
              </div>
            </div>
          ))}
        </div>
        <form
          className={styles.composer}
          onSubmit={(event) => {
            event.preventDefault();
            void ask(input);
          }}
        >
          <textarea
            value={input}
            placeholder="Tanyakan seperti pengunjung… (coba juga pertanyaan jebakan)"
            rows={2}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                void ask(input);
              }
            }}
          />
          {busy ? (
            <Button variant="secondary" icon={<Square aria-hidden />} onClick={() => abort.current?.abort()}>
              Stop
            </Button>
          ) : (
            <Button type="submit" variant="primary" icon={<CornerDownLeft aria-hidden />} disabled={!input.trim()}>
              Kirim
            </Button>
          )}
        </form>
        <p className={styles.hint}>
          <Kbd>Enter</Kbd> kirim · <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> baris baru · klik jawaban untuk melihat detailnya
        </p>
      </Card>

      <Card title="Inspektur jawaban" description="Dari mana jawaban dibuat dan berapa biayanya." className={styles.inspector}>
        {!inspected?.debug ? (
          <EmptyState title="Belum ada jawaban">Kirim pertanyaan untuk melihat konteks, token, dan biaya.</EmptyState>
        ) : (
          <dl className={styles.facts}>
            <dt>Pengetahuan</dt>
            <dd>{inspected.debug.knowledgeVersion === 0 ? "Draf" : `Versi v${inspected.debug.knowledgeVersion}`}</dd>
            <dt>Mode konteks</dt>
            <dd>{inspected.debug.contextMode === "full" ? "Seluruh pengetahuan" : "Potongan hasil pencarian"} · ±{fmt.int(inspected.debug.contextTokens)} token</dd>
            <dt>Model</dt>
            <dd>{inspected.usage?.model}</dd>
            <dt>Token</dt>
            <dd className="num">
              {fmt.int(inspected.usage?.inputTokens ?? 0)} input · {fmt.int(inspected.usage?.outputTokens ?? 0)} output
              <br />
              {fmt.int(inspected.usage?.cacheReadTokens ?? 0)} cache read · {fmt.int(inspected.usage?.cacheWriteTokens ?? 0)} cache write
            </dd>
            <dt>Biaya</dt>
            <dd>
              {client ? (
                <>Saldo terpotong Rp {(inspected.usage?.costRupiah ?? inspected.debug.saldoCostRupiah).toLocaleString("id-ID", { maximumFractionDigits: 2 })}</>
              ) : (
                <>
                  Rp {inspected.debug.providerCostRupiah.toLocaleString("id-ID", { maximumFractionDigits: 2 })} biaya penyedia <span className={styles.muted}>(ditanggung Navibiz)</span>
                  <div className={styles.muted}>Di widget, saldo klien terpotong Rp {inspected.debug.saldoCostRupiah.toLocaleString("id-ID", { maximumFractionDigits: 2 })}</div>
                </>
              )}
            </dd>
            <dt>Waktu jawab</dt>
            <dd>{(inspected.debug.latencyMs / 1000).toFixed(1)} detik</dd>
            {inspected.debug.linkage && (
              <>
                <dt>Rujukan (Linkage)</dt>
                <dd>
                  {inspected.debug.linkage.candidates.length === 0 ? (
                    <span className={styles.muted}>Tidak ada kandidat yang cocok dengan pertanyaan ini.</span>
                  ) : (
                    <ol className={styles.candidates}>
                      {inspected.debug.linkage.candidates.map((candidate) => (
                        <li key={candidate.id} className={inspected.debug!.linkage!.chosen.includes(candidate.id) ? styles.chosen : ""}>
                          <span>{candidate.title}</span>
                          <span className={styles.muted}>{candidate.related ? "dari butir pengetahuan" : `skor ${candidate.score.toFixed(1)}`}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                  {inspected.debug.linkage.fallback && <div className={styles.muted}>Model tidak memilih; rujukan dipilih otomatis dari kecocokan terkuat.</div>}
                </dd>
              </>
            )}
            {inspected.debug.contextMode === "retrieved" && (
              <>
                <dt>Butir yang dipakai</dt>
                <dd>
                  <ul className={styles.chunks}>
                    {inspected.debug.chunkIds.map((chunkId) => (
                      <li key={chunkId}>{titles.get(chunkId.split("#")[0]!) ?? chunkId}</li>
                    ))}
                  </ul>
                </dd>
              </>
            )}
          </dl>
        )}
      </Card>
    </div>
  );
}
